import {Optional} from '@logistics-frontend/types'
import {
  allPass,
  anyPass,
  applySpec,
  constant,
  defaultTo,
  flow,
  get,
  has,
  identity,
  ifElse,
  isEmpty,
  isFilled,
  isUndefined,
  pipe,
  toDefaultEmptyString,
  toKopecks,
  toMeter,
  toNumber,
  toUndefined,
} from '@logistics-frontend/utils'

import {CargoItemSizes, NddClaimProduct, Product, SddClaimProduct} from 'src/shared/types'

/**
 * Считаем, что поле заполнено если оно не undefined
 */
const isLengthFilled: (product: Optional<Product>) => boolean = flow(get('length'), isFilled)

/**
 * Считаем, что поле заполнено если оно не undefined
 */
const isWidthFilled: (product: Optional<Product>) => boolean = flow(get('width'), isFilled)

/**
 * Считаем, что поле заполнено если оно не undefined
 */
const isHeightFilled: (product: Optional<Product>) => boolean = flow(get('height'), isFilled)

/**
 * Считаем, что если хотя бы одно поле заполнено, значит поле sizes проставляется в товар
 */
const hasSizeProperties: (product: Optional<Product>) => boolean = anyPass([
  allPass([has('length'), isLengthFilled]),
  allPass([has('width'), isWidthFilled]),
  allPass([has('height'), isHeightFilled]),
])

/**
 * Проверяем, если размер не заполнен (пустая строка или undefined)
 */
const isSizeIncorrect: (size: Optional<string>) => boolean = anyPass([isUndefined, isEmpty])

const parseSize: (size: Optional<string>) => Optional<number> = ifElse(isSizeIncorrect, toUndefined, toMeter)

const toSizes: (product: Product) => CargoItemSizes = applySpec<CargoItemSizes>({
  length: flow(get('length'), parseSize),
  width: flow(get('width'), parseSize),
  height: flow(get('height'), parseSize),
})

const toCargoItemSizes: (product: Product) => Optional<Partial<CargoItemSizes>> = ifElse(
  hasSizeProperties,
  toSizes,
  toUndefined,
)

const toDefaultCostValue: (price?: string) => string = ifElse(isEmpty, constant('0'), identity)

const toDefaultWeight: (weight?: string) => Optional<number> = ifElse(
  anyPass([isUndefined, isEmpty]),
  toUndefined,
  Number,
)

export const fromSddExpressProduct: (product: Product) => SddClaimProduct = applySpec<SddClaimProduct>({
  title: flow(get('name'), toDefaultEmptyString),
  size: toCargoItemSizes,
  weight: flow(get('weight'), toDefaultWeight),
  cost_value: flow(get('price'), toDefaultCostValue),
  cost_currency: constant('RUB'),
  quantity: flow(get('count'), defaultTo('1'), toNumber),
})

function toBarcode(barcode?: string, parcelIndex = 0) {
  return barcode ? `${barcode}-${parcelIndex}` : ''
}

export function fromNddProduct(
  product: Product,
  barcode?: string,
  parcelIndex = 0,
  productIndex = 0,
  isPaymentGoods = false,
): NddClaimProduct {
  const article = get('vendorCode', product)

  return {
    article: article ? article : String(productIndex + 1),
    count: pipe(product, get('count'), defaultTo(1), toNumber),
    name: pipe(product, get('name'), toDefaultEmptyString),
    place_barcode: toBarcode(barcode, parcelIndex),
    barcode: toBarcode(barcode, parcelIndex),
    physical_dims: {
      // @ts-expect-error - нет такого свойства
      weight_gross: 0,
      predefined_volume: 0,
    },
    billing_details: {
      unit_price: isPaymentGoods ? pipe(product, get('price'), toNumber, toKopecks, Math.round) : 0,
      assessed_unit_price: pipe(product, get('estimatedCost'), toNumber, toKopecks, Math.round),
      nds: get('nds', product),
    },
  }
}
